import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";

/**
 * MarketplaceRulesGate — iter80.
 *
 * Blocks the current attempt to perform an operational Marketplace action
 * (create listing, edit listing, contact user) until the adult user has
 * accepted the CURRENT version of the Marketplace Rules. Called imperatively
 * via `useMarketplaceRulesGate()` — see hook below.
 *
 * 14–17 users never see this modal because the read-only browse endpoints
 * do not require acceptance and every operational endpoint is blocked at
 * the server for them anyway (`_require_adult`).
 */
export function MarketplaceRulesGate({ open, onAccept, onCancel }) {
  const { t } = useI18n();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!checked) { toast.error(t("mkt_rules_gate_required")); return; }
    setBusy(true);
    try {
      await api.post("/auth/accept-marketplace-rules");
      toast.success(t("mkt_rules_gate_accepted_toast"));
      onAccept?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("mkt_rules_gate_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setChecked(false); onCancel?.(); } }}>
      <DialogContent
        className="max-w-md"
        data-testid="mkt-rules-gate"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
            {t("mkt_rules_gate_kicker")}
          </div>
          <DialogTitle className="flex items-center gap-2 mt-1">
            <ScrollText size={18} className="text-emerald-700" />
            {t("mkt_rules_gate_title")}
          </DialogTitle>
          <DialogDescription className="pt-2 leading-relaxed">
            {t("mkt_rules_gate_desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 mb-4">
          <Link
            to="/marketplace-rules"
            target="_blank"
            data-testid="mkt-rules-gate-read"
            className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 underline"
          >
            {t("mkt_rules_gate_read")} →
          </Link>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="mkt-rules-gate-checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span>{t("mkt_rules_gate_checkbox")}</span>
        </label>

        <DialogFooter className="mt-5">
          <Button
            variant="outline"
            data-testid="mkt-rules-gate-cancel"
            onClick={() => { setChecked(false); onCancel?.(); }}
            disabled={busy}
            className="rounded-full"
          >
            {t("mkt_rules_gate_cancel")}
          </Button>
          <Button
            data-testid="mkt-rules-gate-accept"
            onClick={submit}
            disabled={!checked || busy}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
          >
            {busy ? "..." : t("mkt_rules_gate_accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Imperative hook. Usage:
 *
 *   const { requireMarketplaceRules, gateElement } = useMarketplaceRulesGate();
 *   const onSubmit = async () => {
 *     const ok = await requireMarketplaceRules();
 *     if (!ok) return;   // user cancelled or hit an error
 *     await api.post("/marketplace/listings", ...);
 *   };
 *   return <>{ ...form... } {gateElement}</>;
 *
 * If the current user already has `marketplace_rules_accepted` truthy in
 * their `/auth/me` payload, the promise resolves immediately with `true`
 * and NO modal is shown — no unnecessary UI friction.
 */
export function useMarketplaceRulesGate() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ open: false, resolver: null });

  const requireMarketplaceRules = () => {
    return new Promise((resolve) => {
      if (user?.marketplace_rules_accepted) {
        resolve(true);
        return;
      }
      setState({ open: true, resolver: resolve });
    });
  };

  const gateElement = (
    <MarketplaceRulesGate
      open={state.open}
      onAccept={async () => {
        try { await refresh(); } catch {}
        state.resolver?.(true);
        setState({ open: false, resolver: null });
      }}
      onCancel={() => {
        state.resolver?.(false);
        setState({ open: false, resolver: null });
      }}
    />
  );

  return { requireMarketplaceRules, gateElement };
}

export default MarketplaceRulesGate;
